import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { DocMeta } from "../../docs/loader";
import MediaRenderer from "./MediaRenderer";
import { useState } from "react";
import PlatformNote from "./PlatformNote";

export type MarkdownPageProps = {
  content: string;
  meta: DocMeta;
};

const MarkdownPage: React.FC<MarkdownPageProps> = ({ content, meta }) => {
  const AnchorPermalink: React.FC<{ id: string }> = ({ id }) => {
    const [copied, setCopied] = useState(false);
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      const url = `${window.location.origin}${window.location.pathname}#${id}`;
      // Optimistically show feedback immediately
      setCopied(true);
      navigator.clipboard
        .writeText(url)
        .catch(() => {})
        .finally(() => {
          setTimeout(() => setCopied(false), 1200);
        });
      // Don't change hash here to avoid interrupting feedback; users can click the heading text to navigate
    };
    return (
      <button
        onClick={handleClick}
        aria-label="Copy permalink"
        className="ml-2 inline-flex items-center rounded px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 text-sm"
      >
        {copied ? "Copied" : "#"}
      </button>
    );
  };
  const CodeBlock: React.FC<{
    inline?: boolean;
    className?: string;
    children?: React.ReactNode;
  }> = ({ inline, className, children }) => {
    const [copied, setCopied] = useState(false);
    const codeText =
      typeof children === "string" ? children : String(children ?? "");
    if (inline) return <code className={className}>{children}</code>;
    const handleCopy = () => {
      setCopied(true);
      navigator.clipboard.writeText(codeText).finally(() => {
        setTimeout(() => setCopied(false), 1200);
      });
    };
    return (
      <div className="relative group/code">
        <pre className={className}>
          <code>{children}</code>
        </pre>
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700"
          aria-label="Copy code"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    );
  };
  return (
    <article className="prose prose-slate dark:prose-invert max-w-3xl">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({ href, children }) => (
            <a
              href={href}
              target={href && href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
            >
              {children}
            </a>
          ),
          code: (props: any) => <CodeBlock {...props} />,
          img: ({ src, alt, title }) => (
            <MediaRenderer
              src={src || ""}
              alt={alt || ""}
              title={title || undefined}
              meta={meta}
            />
          ),
          blockquote: ({ children }) => {
            const text = String(children);
            // Support callout syntax: > [!NOTE]
            if (text.startsWith("[!")) {
              const m = text.match(/^\[!(\w+)\]/);
              const kind = m?.[1]?.toLowerCase();
              const tone =
                kind === "warning"
                  ? "amber"
                  : kind === "tip"
                  ? "emerald"
                  : "blue";
              return (
                <div
                  className={`not-prose border-l-4 pl-3 py-2 rounded bg-${tone}-50 border-${tone}-400 text-${tone}-800 dark:bg-${tone}-900/30 dark:text-${tone}-200`}
                >
                  {children}
                </div>
              );
            }
            return <blockquote>{children}</blockquote>;
          },
          p: ({ node, children }) => {
            const raw = String(children);
            const match = raw.match(/^\{platforms:\s*(\[[^\]]+\])\}\s*(.*)$/s);
            if (match) {
              try {
                // eslint-disable-next-line no-eval
                const platforms = eval(match[1]) as string[];
                const text = match[2];
                return (
                  <PlatformNote platforms={platforms as any}>
                    {text}
                  </PlatformNote>
                );
              } catch {
                return <p>{children}</p>;
              }
            }
            return <p>{children}</p>;
          },
          h2: ({ node, ...props }) => {
            const text = String(props.children || "");
            const id = text
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, "")
              .trim()
              .replace(/\s+/g, "-");
            return (
              <h2 id={id} {...props} className="group scroll-mt-24">
                <a href={`#${id}`} className="no-underline">
                  {props.children}
                </a>
                <AnchorPermalink id={id} />
              </h2>
            );
          },
          h3: ({ node, ...props }) => {
            const text = String(props.children || "");
            const id = text
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, "")
              .trim()
              .replace(/\s+/g, "-");
            return (
              <h3 id={id} {...props} className="group scroll-mt-24">
                <a href={`#${id}`} className="no-underline">
                  {props.children}
                </a>
                <AnchorPermalink id={id} />
              </h3>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </article>
  );
};

export default MarkdownPage;
